﻿module Illy.SafeStack.Client.Text

open System 
open Elmish
open Fable.React
open Fable.React.Props
open Fable.Core.JsInterop
open Illy.SafeStack.Client


type DraggableDirection =
    | Horizontal
    | Vertical
    | Both
    | Neither

module DraggableDirection = 
    let directionStyle = 
        function
        | Horizontal -> "horizontal"
        | Vertical -> "vertical"
        | Both -> "both"
        | Neither -> "none"

type TextAreaSettings = 
    {
        Rows: int
        Draggable: DraggableDirection
    }

module TextAreaSettings = 
    let defaultOptions() = 
        {
            Rows = 4
            Draggable = DraggableDirection.Vertical
        }

type TextType = 
    | TextBox
    | Password
    | TextArea of TextAreaSettings

type CustomSettings = 
    {
        TextType: TextType
        InputGroupCSSClasses: string list
    }

module CustomSettings = 
    let defaultOptions() = 
        {
            TextType = TextType.TextBox
            InputGroupCSSClasses = []
        }

type Control = 
    {
        Id: string 

        Label: string 
        Placeholder: string option
        MaxLength: float
        Mandatory: bool 

        Error: string option
        Warning: string option 

        Text: string option
    }

type Model = ControlState<Control, CustomSettings> 

type Msg = 
    | TextChanged of string

let private initControl id label placeholder maxLength mandatory text = 
    {
        Id = id
        
        Label = label
        Placeholder = placeholder
        MaxLength = maxLength
        Mandatory = mandatory
        
        Error = None
        Warning = None
        
        Text = text
    }

let init id label placeholder maxLength mandatory text settings : Model = 
    {
        Control = initControl id label placeholder maxLength mandatory text
        Settings = ControlSettings.defaultOptions
        CustomSettings = settings 
    }

let private updateControl control msg = 
    match msg with 
    | TextChanged v -> 
        if float v.Length > control.MaxLength then 
            {control with 
                Error = Some (sprintf "Max length is restricted to %d." (int control.MaxLength))
            }, Cmd.none

        else 
            {control with 
                Text = 
                    if String.IsNullOrEmpty v then None 
                    else Some v
                Error = None 
            }, Cmd.none

let update msg (model: Model) =
    let m, c = updateControl model.Control msg
    {model with Control = m}, c


let private textBoxInput inputType (control: Control) dispatch loading =
    input [ 
        Id control.Id 
        ClassName (jQueryUI.addSkeletonScreen "form-control" None loading)
        Type inputType
        OnChange (fun ev -> if not loading then dispatch (TextChanged !!ev.target?value))
        Placeholder (match control.Placeholder with Some pl when not loading -> pl | _ -> "" )
        MaxLength control.MaxLength
        Elmish.React.Helpers.valueOrDefault (control.Text |> Option.defaultValue "")
        Disabled loading
    ] 

let private textAreaInput (settings: TextAreaSettings) (control: Control) dispatch loading = 
    textarea [ 
        Id control.Id
        ClassName (jQueryUI.addSkeletonScreen "form-control" None loading)
        OnChange 
            (fun ev -> if not loading then dispatch (TextChanged !!ev.target?value))
        Placeholder (match control.Placeholder with Some pl when not loading -> pl | _ -> "")
        MaxLength control.MaxLength
        Elmish.React.Helpers.valueOrDefault (control.Text |> Option.defaultValue "")
        Rows settings.Rows
        Disabled loading
        Style [ 
            Resize (DraggableDirection.directionStyle settings.Draggable)
        ] 
    ] []

let textInput loading (model: Model) dispatch = 
    let control = model.Control
    match model.CustomSettings.TextType with 
    | TextType.TextBox -> textBoxInput "text" control dispatch loading
    | TextType.Password -> textBoxInput "password" control dispatch loading
    | TextType.TextArea ts -> textAreaInput ts control dispatch loading

let text loading (model: Model) dispatch = 
    let control = model.Control
    jQueryUI.inputGroupWithClasses model.CustomSettings.InputGroupCSSClasses [
        div [ ClassName "label" ] [
            jQueryUI.labelWithMandatory control.Id control.Label control.Error control.Warning loading control.Mandatory
        ]
        textInput loading model dispatch
    ]
    
//utility functions
let resetText (model: Model) = 
    {model with 
        Control = 
            {model.Control with 
                Error = None 
                Warning = None
                Text = None
            }
    }

let resetValidations (model: Model) = 
    {model with 
        Control = 
            {model.Control with 
                Error = None 
                Warning = None
            }
    }

let setValidations (model: Model) errors warnings = 
    {model with 
        Control = 
            {model.Control with 
                Error = errors 
                Warning = warnings
            }
    }

let toString (model: Model) = model.Control.Text
